import { createContext, useContext, useMemo, useState } from 'react';
import type { InitParamsType, InternalInitParamsType } from '@onramp/types/initParams';

export type InitParamsContextType = {
  initParams: InitParamsType;
  internalInitParams: InternalInitParamsType;
};

const defaultInternalInitParams = {
  clientAppDetails: {
    appId: '',
    oauthId: '',
    domains: [],
    displayName: '',
    isActive: false,
    isSecureInitRequired: false,
    isThirdParty: false,
    maxSendLimitUsd: '',
    maxGuestCheckoutLimitUsd: '',
    maxTransactionCount: '',
    maxAmountPerTransaction: '',
    currentTransactionCount: '',
  },
  userUuid: undefined,
};

export const InitParamsContext = createContext<InitParamsContextType>({
  initParams: {
    appId: '',
    destinationWallets: [],
    disableCryptoSend: false,
  },
  internalInitParams: defaultInternalInitParams,
});

export const InitParamsProvider: React.FC<{
  initParamsFromServer: InitParamsType;
  internalInitParamsFromServer: InternalInitParamsType | undefined;
}> = ({ children, initParamsFromServer, internalInitParamsFromServer }) => {
  const [initParams] = useState<InitParamsType>(initParamsFromServer ?? {});
  const [internalInitParams] = useState<InternalInitParamsType>(
    // If params are missing, they're handled via initParamErrors
    internalInitParamsFromServer as InternalInitParamsType,
  );

  const value = useMemo(
    () => ({
      initParams,
      internalInitParams,
    }),
    [initParams, internalInitParams],
  );

  return <InitParamsContext.Provider value={value}>{children}</InitParamsContext.Provider>;
};

export const useInitParams = () => {
  const context = useContext(InitParamsContext);
  if (context === undefined) {
    throw new Error('useInitParams must be used within a InitParamsProvider');
  }
  return context;
};
